import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { createBrowserRouter, Link, Outlet, RouterProvider, useRouteError } from "react-router";
import { Bell, Download, Inbox, LayoutDashboard, ListFilter, Lock, Mail, Settings, ShieldAlert } from "lucide-react";
import { AppShell, MailSyncPanel, NotificationBell, migrateLegacyPushRegistrations, type ShellNavItem } from "@ozpanel/kit";
import { ErrorScreen, sound, useAuth, useStandalone, useToast } from "@ozpanel/ui";
import { useMailboxApi } from "./api";
import { useAppIdentity } from "./appIdentity";
import { SetupPrompt } from "./SetupPrompt";
import { MAILBOX_PAGES, BOTTOM_TABS } from "./pages";
import { WebmailView } from "./WebmailView";
import { SettingsPanel } from "./SettingsPanel";
import { Overview } from "./screens/Overview";
import { Rules, SpamAndHeld } from "./screens/Rules";
import { Security } from "./screens/Security";
import { Notifications } from "./screens/Notifications";

// The mailbox app: real addresses for every page (Back works, a page can be linked), one frame
// (kit AppShell) shared with the account console, and — in the installed app on a phone — a bottom
// tab bar for the four places used most, with More for the rest.
//
// A new-mail push opens "/?open=mail" (mail_push_trigger.go's newMailNotificationURL): main.tsx
// turns that into /mail before the router starts, and /mail reads the CURRENT webmail default,
// live — never one cached from whenever the mail arrived.

const ICONS: Record<string, React.ReactNode> = {
  mail: <Inbox size={20} />,
  overview: <LayoutDashboard size={20} />,
  spam: <ShieldAlert size={20} />,
  rules: <ListFilter size={20} />,
  import: <Download size={20} />,
  notifications: <Bell size={20} />,
  security: <Lock size={20} />,
  settings: <Settings size={20} />,
};

const NAV: ShellNavItem[] = MAILBOX_PAGES.map((p) => ({
  key: p.key,
  to: "/" + p.path,
  end: p.path === "",
  label: p.label,
  icon: ICONS[p.key],
}));

function MailPage() {
  const api = useMailboxApi();
  const webmailDefault = useQuery({ queryKey: ["webmail-default"], queryFn: api.getWebmailDefault });
  // The view lists every client and handles its own loading. It does not wait on the default: a
  // failure to read one preference should not hide webmail entirely.
  return <WebmailView client={webmailDefault.data?.default ?? "roundcube"} />;
}

function Shell() {
  const { who, logout, impersonating, stopImpersonating } = useAuth();
  const api = useMailboxApi();
  const { notify } = useToast();
  const standalone = useStandalone();
  const identity = useAppIdentity();

  // Unlock-on-first-click is armed once, for the whole app's lifetime, so a tap anywhere before
  // opening mail already allows the new-mail sound later.
  React.useEffect(() => {
    sound.armUnlock();
  }, []);

  // One push worker per app. A subscription still held by the legacy origin-wide worker (/sw.js) is
  // moved onto this app's worker on the next open — never dropped: if the move cannot complete,
  // the old worker keeps delivering and nothing is changed (kit/webpush.ts).
  React.useEffect(() => {
    const baseURL = new URL(import.meta.env.BASE_URL, window.location.origin).toString();
    void migrateLegacyPushRegistrations({
      baseURL,
      vapidKey: async () => (await api.vapidKey()).public_key,
      save: async (endpoint, keys, ua) => {
        await api.subscribePush(endpoint, keys, ua);
      },
    })
      .then((r) => {
        if (r.kind === "moved") notify({ kind: "info", title: "Notifications reconnected to this app" });
      })
      .catch(() => {
        /* Left exactly as it was; the Notifications page shows this device's real state. */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell
      brand={
        <span className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-[9px] bg-accent text-onaccent" aria-hidden>
            <Mail size={17} />
          </span>
          <span className="hidden max-w-40 truncate text-[16px] font-semibold sm:inline">{identity.shortName}</span>
        </span>
      }
      nav={NAV}
      homeHref="/"
      bell={<NotificationBell audience="customer" />}
      bottomTabs={standalone ? BOTTOM_TABS : undefined}
      account={{
        email: who?.email,
        impersonating,
        onStopImpersonating: async () => {
          await stopImpersonating();
          window.location.assign("/");
        },
        onSignOut: async () => {
          await logout();
          window.location.assign("/");
        },
      }}
    >
      <SetupPrompt />
      <Outlet />
    </AppShell>
  );
}

function RouteError() {
  return <ErrorScreen error={useRouteError()} homeHref={import.meta.env.BASE_URL} />;
}

function NotFound() {
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <div className="text-[17px] font-semibold">There is no page here</div>
      <p className="mt-2 text-[13.5px] text-muted">The address may be old or mistyped.</p>
      <Link to="/" className="mt-4 inline-block text-[13.5px] font-medium text-accent hover:underline">
        Go to the overview
      </Link>
    </div>
  );
}

// The router is built on the app's FIRST RENDER, never at import. createBrowserRouter reads the address
// when it is called, and main.tsx's canonicalise() rewrites "/" and "/app" to <base>/… with
// history.replaceState — which no router hears. Built at import, it ran before canonicalise (imports are
// evaluated first), kept "/", matched nothing under basename "/ozpanel" and rendered a blank page after
// every sign-in from webmail.<domain>/ until a reload (admin@ozpanel.net, 2026-11-03). Created once,
// so navigation state survives re-renders.
let router: ReturnType<typeof createBrowserRouter> | undefined;
const routerOnce = () => (router ??= createBrowserRouter(
  [
    {
      path: "/",
      element: <Shell />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: <Overview /> },
        { path: "mail", element: <MailPage /> },
        { path: "rules", element: <Rules /> },
        { path: "spam", element: <SpamAndHeld /> },
        // Other accounts copied or kept in sync into THIS mailbox — the same panel the account
        // console shows a tenant, scoped by the server to the signed-in mailbox.
        { path: "import", element: <MailSyncPanel audience="me" /> },
        { path: "notifications", element: <Notifications /> },
        { path: "security", element: <Security /> },
        { path: "settings", element: <SettingsPanel /> },
        { path: "*", element: <NotFound /> },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL.replace(/\/$/, "") || undefined },
));

export function MailboxApp() {
  return <RouterProvider router={routerOnce()} />;
}
